import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useSmartImageUpload } from '../../services/image';
import { show_app_toast } from '../../services/appToast';
import { infer_event_poster_type } from '../../services/eventPoster';
import {
    DEFAULT_ACCEPT,
    fileUploadAcceptsImages,
    fileUploadHasStoredValue,
    fileUploadHintKey,
    fileUploadPreviewSrc,
    shouldKeepFileUploadValue,
} from './fileUploadUtils';

import './FileUpload.css';

const FileUpload = props => {
    const { t } = useTranslation();
    const optimizePreset = props.optimizePreset || null;
    const preservePng = props.preservePng;
    const presetOverrides = props.presetOverrides || null;
    const rejectOnFailure = !!props.rejectOnProcessFailure;
    const disabled = !!props.disabled;
    const multiple = !!props.multiple;
    const inferEventPosterType = !!props.inferEventPosterType;
    const onAfterChange = props.onAfterChange;
    const title = props.title || null;
    const hint = props.hint || null;
    const [fileName, setFileName] = useState('');
    const [busy, setBusy] = useState(false);

    const currentValue = props.input?.value;
    const hasValue = fileUploadHasStoredValue(currentValue);
    const previewSrc = fileUploadPreviewSrc(currentValue);
    const accept = props.accept || DEFAULT_ACCEPT;
    const isImage = fileUploadAcceptsImages(accept);

    const getBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    const onError = useCallback(() => {
        setFileName('');
        show_app_toast(t('image_upload.invalid_file'), { variant: 'error' });
    }, [t]);

    const { handleFiles } = useSmartImageUpload({
        preset: optimizePreset || 'setting',
        preservePng,
        presetOverrides: presetOverrides || undefined,
        rejectOnFailure,
        onError: rejectOnFailure ? onError : undefined,
    });

    const processFiles = useCallback(async (files) => {
        const { input } = props;
        const list = (Array.isArray(files) ? files : []).filter(Boolean);
        if (!list.length) {
            setFileName('');
            input.onChange(null);
            return;
        }

        setFileName(list.map((f) => f.name).filter(Boolean).join(', '));
        setBusy(true);
        try {
            let items;
            if (optimizePreset) {
                const results = await handleFiles(list);
                items = results.map(({ dataUrl, report }) => {
                    const item = { dataUrl };
                    if (inferEventPosterType) {
                        item.type = infer_event_poster_type(
                            report?.originalWidth,
                            report?.originalHeight,
                        );
                    }
                    return item;
                });
            } else {
                items = [];
                for (const file of list) {
                    items.push({ dataUrl: await getBase64(file) });
                }
            }
            if (!items.length) {
                setFileName('');
                input.onChange(null);
                return;
            }

            // Redux-form field stays a single data URL (API contract for new_poster).
            input.onChange(items[items.length - 1].dataUrl);
            if (typeof onAfterChange === 'function') {
                const payload = multiple ? items : items[0];
                await onAfterChange(payload);
            }
            if (!shouldKeepFileUploadValue(onAfterChange)) {
                // Event posters persist immediately; lingering names look like a pending upload.
                setFileName('');
                input.onChange(null);
            }
        } finally {
            setBusy(false);
        }
    }, [handleFiles, optimizePreset, onAfterChange, multiple, inferEventPosterType, props]);

    const onDrop = useCallback((acceptedFiles) => {
        const files = Array.isArray(acceptedFiles) ? acceptedFiles : [];
        processFiles(multiple ? files : files.slice(0, 1));
    }, [processFiles, multiple]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple,
        disabled: disabled || busy,
        noKeyboard: false,
    });

    const { input: { name }, required } = props;
    const inputId = props.id || name;
    const rawClass = props.className || props.classname || '';
    const extraClass = rawClass
        .split(/\s+/)
        .filter((c) => c && c !== 'form-control')
        .join(' ');
    const rootClass = [
        'FileUpload',
        isDragActive ? 'FileUpload--active' : '',
        (disabled || busy) ? 'FileUpload--disabled' : '',
        extraClass,
    ].filter(Boolean).join(' ');
    const defaultHint = t(fileUploadHintKey({ isImage, multiple }));

    return (
        <div {...getRootProps({ className: rootClass })}>
            <input
                {...getInputProps({
                    id: inputId,
                    name,
                    accept,
                    required: false,
                    disabled: disabled || busy,
                })}
            />
            {required ? (
                <input
                    type="text"
                    className="FileUpload-requiredSentinel"
                    value={hasValue ? '1' : ''}
                    required
                    tabIndex={-1}
                    aria-hidden="true"
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => {}}
                    onInvalid={(e) => {
                        const dropzone = e.currentTarget.closest('.FileUpload');
                        if (typeof dropzone?.scrollIntoView === 'function') {
                            dropzone.scrollIntoView({
                                block: 'center',
                                behavior: 'auto',
                            });
                        }
                    }}
                />
            ) : null}
            {title ? <div className="FileUpload-title">{title}</div> : null}
            <p className="FileUpload-hint">
                {busy
                    ? t('common.loading')
                    : (hint || defaultHint)}
            </p>
            {!busy && previewSrc ? (
                <img
                    src={previewSrc}
                    alt=""
                    className="FileUpload-preview"
                />
            ) : null}
            {!busy && !previewSrc && fileName ? (
                <div className="FileUpload-fileName mt-2">
                    <i className="fa fa-file-o" aria-hidden="true" />
                    &nbsp;<b>{ fileName }</b>
                </div>
            ) : null}
            {!busy && !previewSrc && !fileName && hasValue ? (
                <div className="FileUpload-fileName mt-2">
                    <i className="fa fa-file-o" aria-hidden="true" />
                    &nbsp;<b>{ t('common.file_selected') }</b>
                </div>
            ) : null}
        </div>
    );
};

export default FileUpload;
